import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, inboxApi, type TenantChannel, disconnectExplanation, formatIst } from '@wa/shared';

/**
 * The account's WhatsApp numbers, and the one thing about them the team may
 * change here: what they call each one.
 *
 * Everything else on a number belongs to Meta. The display name customers see
 * is changed in WhatsApp Manager and reviewed there; the quality rating and the
 * sending tier are Meta's verdicts. This screen shows them and edits none of
 * them, because a field that looks editable and is quietly overwritten on the
 * next sync is worse than no field.
 */
export function NumbersPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savedId, setSavedId] = useState<number | null>(null);

  // Disconnected numbers included: a number that vanished from this list was
  // how a client learned theirs had been unlinked. It stays, says when and
  // why, and says who to call.
  const channels = useQuery({ queryKey: ['channels', 'all'], queryFn: inboxApi.allChannels });

  const save = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) => inboxApi.setChannelLabel(id, label),
    onSuccess: (_, { id }) => {
      setError(null);
      setSavedId(id);
      setTimeout(() => setSavedId((current) => (current === id ? null : current)), 2000);
      setDrafts((current) => { const next = { ...current }; delete next[id]; return next; });
      // Every dropdown that names a number reads this query.
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save that name.'),
  });

  const draftOf = (c: TenantChannel) => drafts[c.id] ?? c.label ?? '';

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          {channels.data ? `${channels.data.length} ${channels.data.length === 1 ? 'number' : 'numbers'}` : 'Numbers'}
        </h2>

        {channels.isPending && <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">Loading…</p>}
        {channels.isError && <p className="px-3 py-3 text-small text-(--color-bad) sm:px-4">Could not load the numbers.</p>}

        <ul className="divide-y divide-(--color-line)">
          {channels.data?.map((c) => {
            const draft = draftOf(c);
            const dirty = draft.trim() !== (c.label ?? '');
            return (
              <li key={c.id} className="px-3 py-3 sm:px-4">
                <form
                  className="flex flex-wrap items-end gap-x-4 gap-y-2"
                  onSubmit={(e) => { e.preventDefault(); if (dirty && !save.isPending) save.mutate({ id: c.id, label: draft.trim() }); }}
                >
                  <div className="min-w-0 flex-1 basis-52">
                    <p className="font-mono text-small font-medium">
                      {c.displayPhoneNumber}
                      {!c.isActive && (
                        <span className="ml-2 rounded-full bg-(--color-warn)/15 px-2 py-0.5 font-sans text-micro font-semibold text-(--color-warn)">
                          {c.disconnectedAt ? 'disconnected' : 'off'}
                        </span>
                      )}
                    </p>
                    {!c.isActive && c.disconnectedAt && (
                      <p className="mt-0.5 text-micro leading-snug text-(--color-ink-soft)">
                        Disconnected from WhatsApp {formatIst(c.disconnectedAt)} — {disconnectExplanation(c)}. Nothing can be sent from it until it is reconnected; contact PSM Softtech.
                      </p>
                    )}
                    <p className="text-micro text-(--color-muted)">
                      Shown to customers as <b>{c.verifiedName ?? '—'}</b>
                      {c.qualityRating && <> &middot; quality {c.qualityRating.toLowerCase()}</>}
                      {c.messagingLimitTier && <> &middot; {c.messagingLimitTier.replace('TIER_', '').toLowerCase()} a day</>}
                    </p>
                  </div>

                  <label className="block min-w-0 flex-1 basis-56">
                    <span className="mb-1 block text-micro font-medium uppercase tracking-wide text-(--color-faint)">What your team calls it</span>
                    <input
                      value={draft}
                      maxLength={60}
                      placeholder="Marketing, Orders, Support…"
                      onChange={(e) => setDrafts((current) => ({ ...current, [c.id]: e.target.value }))}
                      className="w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)"
                    />
                  </label>

                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={!dirty || save.isPending}
                      className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
                    >
                      {save.isPending && save.variables?.id === c.id ? 'Saving…' : 'Save'}
                    </button>
                    {savedId === c.id && <span role="status" className="text-small text-(--color-good)">Saved.</span>}
                  </div>
                </form>
              </li>
            );
          })}
        </ul>

        <p className="border-t border-(--color-line) px-3 py-2.5 text-micro text-(--color-muted) sm:px-4">
          The name customers see is Meta&rsquo;s display name, changed in WhatsApp Manager and reviewed by Meta.
          The name here is only for your team &mdash; it appears first wherever a number is picked, with the
          number after it, so two numbers with the same company name can be told apart.
        </p>
      </section>
    </div>
  );
}
